import React, { useEffect, useState } from "react";
import AddBaseModal from "../../../../../components/add-base-modal/AddBaseModal";
import { showMessage, useApi } from "../../../../../services/helpers";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import CheckoutForm from "./CheckoutForm";
import MDBox from "../../../../../components/MDBox";
import MDTypography from "../../../../../components/MDTypography";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "pk_test_your_publishable_key");

const PaymentModal = ({ open, onClose, onPaymentSuccess, requestedOrder, projectId }) => {
  const api = useApi();
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [smStripeWarning, setSmStripeWarning] = useState(null);

  // Backend-computed flag (Order.is_stripe_order) — the caller (ShareQuoteSection)
  // already gates rendering this modal on it, so this is a defensive re-check rather
  // than the primary gate.
  const isStripeEnabled = !!requestedOrder?.is_stripe_order;

  useEffect(() => {
    if (open) {
      setSmStripeWarning(null);
      createPaymentIntent();
    }
  }, [open]);

  const createPaymentIntent = async () => {
    if (!requestedOrder?.id) return;

    setLoading(true);

    try {
      const result = await api.createPaymentIntent(projectId, {}, requestedOrder.id);

      if (result.kind === "ok") {
        setClientSecret(result.data.client_secret);
      } else {
        const d = result.errors ?? result.data;
        if (d?.sm_stripe_error) {
          setSmStripeWarning(d.detail);
        } else {
          const msg = typeof d === 'string' ? d
            : Array.isArray(d) ? d[0]
            : d?.detail || d?.non_field_errors?.[0]
            || (d && typeof d === 'object' ? Object.values(d).flat()[0] : null)
            || "Payment setup failed. Please try again.";
          showMessage(msg, 'warning');
          onClose();
        }
      }
    } catch (error) {
      showMessage("Payment setup failed. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Called once stripe.confirmCardPayment() resolves client-side. Synchronously
  // confirms with the backend (a backstop alongside the async webhook) so the order's
  // new phase is reflected immediately instead of waiting on the webhook to land.
  const handleCheckoutSuccess = async () => {
    setConfirming(true);
    try {
      await api.confirmPayment(projectId, requestedOrder.id);
      onPaymentSuccess?.();
    } catch (error) {
      showMessage("Payment succeeded but we couldn't confirm it — refresh to see the latest status.", "warning");
    } finally {
      setConfirming(false);
      onClose();
    }
  };

  return (
    <AddBaseModal
      open={open}
      handleClose={onClose}
      showActions={false}
      modalContainer={{ width: 650 }}
    >
      <MDBox p={3} minHeight={300}>
        {(loading || confirming) && (
          <MDTypography>
            {confirming ? "Confirming your payment..." : "Preparing secure payment..."}
          </MDTypography>
        )}

        {!loading && !confirming && smStripeWarning && (
          <MDBox
            sx={{
              p: "14px 16px",
              borderRadius: "8px",
              bgcolor: "#fff8e1",
              border: "1px solid #ffe082",
              display: "flex",
              flexDirection: "column",
              gap: "6px",
            }}
          >
            <MDTypography variant="body2" fontWeight="bold" sx={{ color: "#7a5a00" }}>
              ⚠️ Payment unavailable
            </MDTypography>
            <MDTypography variant="caption" sx={{ color: "#7a5a00", lineHeight: 1.6 }}>
              {smStripeWarning}
            </MDTypography>
          </MDBox>
        )}

        {!loading && !confirming && !smStripeWarning && !isStripeEnabled && (
          <MDTypography color="error">
            Payment is not available because no products support Stripe.
          </MDTypography>
        )}

        {!loading && !confirming && !smStripeWarning && clientSecret && isStripeEnabled && (
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <CheckoutForm clientSecret={clientSecret} onSuccess={handleCheckoutSuccess} />
          </Elements>
        )}
      </MDBox>
    </AddBaseModal>
  );
};

export default PaymentModal;
